import { Currency } from '@seapoint/server/enums';
import { useState } from 'react';
import { Drawer, DrawerContent } from '../../../../design-system/drawer/drawer';
import { Stack } from '../../../../design-system/stack/stack';
import { CategoryView, VendorView } from '../../../../types/entity-types';
import { getTransactionSummaryMetrics } from '../../../transactions/components/transaction-details-drawer/utils/get-transaction-summary-metrics';
import { VendorTransactionsSummary } from '../../../transactions/components/transaction-details-drawer/vendor-section/vendor-transactions-summary';
import { getVendorFinancials } from '../../utils/get-vendor-financials';
import { AccountDetails } from './account-details/account-details';
import { EditVendor } from './edit-vendor/edit-vendor';
import { Header } from './header/header';
import { RecentTransactions } from './recent-transactions/recent-transactions';
import { TransactionChart } from './transactions-chart/transaction-chart';

type VendorDetailsDrawerProps = {
  isOpen: boolean;
  vendor: VendorView;
  baseCurrency: Currency;
  onClose: () => void;
  categories: CategoryView[];
  vendors: VendorView[];
  onViewAllTransactions?: () => void;
  onSelectTransaction?: (transactionId: string) => void;
};

export const VendorDetailsDrawer = ({
  isOpen,
  vendor,
  categories,
  vendors,
  baseCurrency,
  onClose,
  onViewAllTransactions,
  onSelectTransaction,
}: VendorDetailsDrawerProps) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const hasTransactions = vendor.transactions && vendor.transactions.length > 0;

  const destination = vendor.transactions?.find((t) => t.beneficiary?.destination)?.beneficiary?.destination;
  const accountDetails = {
    iban: destination?.iban,
    sortCode: destination?.sortCode,
    accountNumber: destination?.accountNumber,
  };

  // TODO: Move this up a level and pass as a prop
  const vendorFinancials = getVendorFinancials(vendor.transactions ?? []);

  const metrics = getTransactionSummaryMetrics(
    vendor.transactions ?? [],
    vendor.transactions?.[0]?.currency ?? baseCurrency,
  );

  return (
    <Drawer open={isOpen} onOpenChange={onClose} direction="right" modal={false}>
      <DrawerContent>
        {isEditMode ? (
          <EditVendor vendor={vendor} categories={categories} vendors={vendors} onBack={() => setIsEditMode(false)} />
        ) : (
          <Stack space={3} className="h-full w-full">
            <Header vendor={vendor} onEditVendor={() => setIsEditMode(true)} onClose={onClose} />
            <Stack space={3} className="h-full w-full overflow-x-hidden overflow-y-auto">
              {accountDetails && <AccountDetails accountDetails={accountDetails} />}
              <VendorTransactionsSummary metrics={metrics} />
              {vendorFinancials.monthlyTotals.length > 0 && <TransactionChart data={vendorFinancials} />}
              {hasTransactions ? (
                <RecentTransactions
                  transactions={vendor.transactions ?? []}
                  onSelectRow={onSelectTransaction}
                  onViewAll={onViewAllTransactions}
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center">
                  <p className="text-base leading-[22px] font-medium tracking-[-0.02em]">No transactions found</p>
                </div>
              )}
            </Stack>
          </Stack>
        )}
      </DrawerContent>
    </Drawer>
  );
};
